import { Action as AccessTokenAction, ActionType as AccessTokenActionType } from '~/AccessToken';
import { ProductSourcePrice } from '~/Api/Product';
import { CzMaxPrice, ProductUpdate, ValidationResult } from '~/Api/User/ProductGroup/CzMaxPrice';
import { ProductPrice } from '~/Product';

import { Action, ActionType } from './Action';
import Price from '~/utils/Price';

export interface CzMaxPriceState {
	request: Promise<any>;
	isFetching: boolean;
	czMaxPrice: CzMaxPrice | null;
}

type UpdateState =
	| {
			name: 'scheduled';
			timeoutId: NodeJS.Timeout;
			packageSize: number | null;
			basketVersion: string;
			products: ProductUpdate[];
	  }
	| { name: 'fetching'; request: Promise<any> | null }
	| { name: 'updated' }
	| { name: 'failed' };

export interface Update {
	groupId: string;
	state: UpdateState;
}

export interface Export {
	groupId: string;
	request: Promise<any>;
	isFetching: boolean;
	validationResult: ValidationResult | null;
}

export interface State {
	byId: { [key in string]?: CzMaxPriceState };
	update: Update | null;
	export: Export | null;
}

function createState(): State {
	return {
		byId: {},
		update: null,
		export: null,
	};
}

export default function reducer(state: State = createState(), action: Action | AccessTokenAction): State {
	switch (action.type) {
		case ActionType.request:
			return {
				...state,
				byId: {
					...state.byId,
					[action.groupId]: {
						request: action.request,
						isFetching: true,
						czMaxPrice: state.byId[action.groupId]?.czMaxPrice ?? null,
					},
				},
			};

		case ActionType.receive: {
			const czMaxPrice = state.byId[action.groupId];
			if (czMaxPrice?.request !== action.request) {
				return state;
			}

			if (action.czMaxPrice === null) {
				const byId = { ...state.byId };
				delete byId[action.groupId];
				return { ...state, byId };
			}

			return {
				...state,
				byId: {
					[action.groupId]: {
						...czMaxPrice,
						isFetching: false,
						czMaxPrice: action.czMaxPrice,
					},
				},
			};
		}

		case ActionType.prune: {
			const byId = { ...state.byId };
			delete byId[action.groupId];
			return { ...state, byId };
		}

		case ActionType.scheduleUpdate: {
			const updateState = state.update?.state;
			if (updateState?.name === 'scheduled') {
				clearTimeout(updateState.timeoutId);
			}

			return {
				...state,
				update: {
					groupId: action.groupId,
					state: {
						name: 'scheduled',
						timeoutId: action.timeoutId,
						packageSize: action.packageSize,
						basketVersion: action.basketVersion,
						products: action.products,
					},
				},
			};
		}

		case ActionType.requestUpdate: {
			return {
				...state,
				update: {
					groupId: action.groupId,
					state: { name: 'fetching', request: action.request },
				},
			};
		}

		case ActionType.receiveUpdate: {
			const updateState = state.update?.state;
			if (updateState?.name === 'fetching' && updateState?.request !== action.request) {
				return state;
			}

			const byId = { ...state.byId };
			const czMaxPriceState = byId[action.groupId];

			if (action.czMaxPrice !== null && czMaxPriceState !== undefined) {
				byId[action.groupId] = {
					...czMaxPriceState,
					czMaxPrice: action.czMaxPrice,
				};
			}

			return {
				...state,
				byId,
				update: {
					groupId: action.groupId,
					state: action.czMaxPrice !== null ? { name: 'updated' } : { name: 'failed' },
				},
				export: null,
			};
		}

		case ActionType.requestXlsCreate:
			return {
				...state,
				export: {
					groupId: action.groupId,
					request: action.request,
					isFetching: true,
					validationResult: null,
				},
			};

		case ActionType.receiveXlsCreate:
			if (state.export?.request !== action.request) {
				return state;
			}

			return {
				...state,
				export: {
					...state.export,
					isFetching: false,
					validationResult: action.validationResult,
				},
			};

		case AccessTokenActionType.receive:
			return action.userChanged ? createState() : state;

		case AccessTokenActionType.receiveLogin:
			return createState();

		default:
			return state;
	}
}

export function selectCzMaxPrice(state: State, groupId: string): CzMaxPriceState | null {
	return state.byId[groupId] ?? null;
}

export function selectExport(state: State, groupId: string): Export | null {
	return state.export?.groupId === groupId ? state.export : null;
}

export function selectCzechExFactoryPrice(czMaxPrice: CzMaxPrice): Price | null {
	let exFactoryPrice: Price | null = null;
	czMaxPrice.products.forEach((product) => {
		if (product.product.dataset.source.country.alpha2 !== 'CZ') {
			return;
		}

		const price = product.product.sourcePrices.find((price) => price.type === 'ex-factory');
		if (price !== undefined) {
			exFactoryPrice = price.price;
		}
	});

	return exFactoryPrice;
}
